import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Plus, Eye, CheckCircle } from "lucide-react";
import {
  useBudgets,
  useBudgetById,
  useCreateBudget,
  useApproveBudget,
  useBudgetVarianceReport,
  usePostableAccounts,
  useCostCentres,
} from "../hooks/useAccounting";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import type { CreateBudgetDto, CreateBudgetLineDto } from "../services/accountingApi";

const FREQUENCY_OPTIONS = [
  { value: "1", label: "Monthly" },
  { value: "2", label: "Quarterly" },
  { value: "3", label: "Annually" },
];

function fmt(n: number) {
  return new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", minimumFractionDigits: 2 }).format(n);
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-KE", { year: "numeric", month: "short", day: "numeric" });
}

// ── Create Budget Dialog ──────────────────────────────────────────────────────

function CreateBudgetDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: accounts } = usePostableAccounts();
  const { data: costCentres } = useCostCentres();
  const createMutation = useCreateBudget();
  const notify = useNotification();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    budgetName: "",
    fiscalYear: currentYear,
    startDate: `${currentYear}-01-01`,
    endDate: `${currentYear}-12-31`,
    frequency: "2",
    description: "",
  });

  const [lines, setLines] = useState<CreateBudgetLineDto[]>([
    { accountId: "", costCentreId: null, periodNumber: 1, budgetedAmount: 0 },
  ]);

  const addLine = () => setLines([...lines, { accountId: "", costCentreId: null, periodNumber: 1, budgetedAmount: 0 }]);

  const updateLine = (idx: number, field: keyof CreateBudgetLineDto, value: string | number | null) => {
    setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const removeLine = (idx: number) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const dto: CreateBudgetDto = {
      budgetName: form.budgetName,
      fiscalYear: form.fiscalYear,
      startDate: new Date(form.startDate).toISOString(),
      endDate: new Date(form.endDate).toISOString(),
      frequency: Number(form.frequency),
      description: form.description || null,
      lines,
    };
    createMutation.mutate(dto, {
      onSuccess: () => { notify.success("Budget created"); onOpenChange(false); },
      onError: (err) => {
        const fe = getFieldErrors(err);
        if (fe) setFieldErrors(fe);
        notify.error(getErrorMessage(err));
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Budget</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Budget Name <span className="text-red-500">*</span></FieldLabel>
              <Input value={form.budgetName} onChange={(e) => setForm({ ...form, budgetName: e.target.value })} required />
              {fieldErrors.budgetName && <p className="text-red-500 text-xs mt-1">{fieldErrors.budgetName.join(", ")}</p>}
            </Field>
            <Field>
              <FieldLabel>Fiscal Year</FieldLabel>
              <Input type="number" value={form.fiscalYear} onChange={(e) => setForm({ ...form, fiscalYear: Number(e.target.value) })} />
            </Field>
            <Field>
              <FieldLabel>Start Date</FieldLabel>
              <DatePicker value={form.startDate} onChange={(v) => setForm({ ...form, startDate: v })} />
            </Field>
            <Field>
              <FieldLabel>End Date</FieldLabel>
              <DatePicker value={form.endDate} onChange={(v) => setForm({ ...form, endDate: v })} />
            </Field>
            <Field>
              <FieldLabel>Frequency</FieldLabel>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Description</FieldLabel>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
          </div>

          <h4 className="font-medium text-sm mt-4">Budget Lines</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead><TableHead>Cost Centre</TableHead>
                <TableHead className="w-24">Period</TableHead><TableHead className="w-32">Amount</TableHead><TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Select value={line.accountId || "none"} onValueChange={(v) => updateLine(idx, "accountId", v === "none" ? "" : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Account" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {accounts?.filter((a) => !a.isGroup).map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={line.costCentreId || "none"} onValueChange={(v) => updateLine(idx, "costCentreId", v === "none" ? null : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {costCentres?.filter((cc) => !cc.isGroup).map((cc) => (
                          <SelectItem key={cc.id} value={cc.id}>{cc.costCentreCode}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input type="number" className="h-8 text-xs" min={1} value={line.periodNumber}
                      onChange={(e) => updateLine(idx, "periodNumber", Number(e.target.value))} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" className="h-8 text-xs" min={0} step={0.01} value={line.budgetedAmount || ""}
                      onChange={(e) => updateLine(idx, "budgetedAmount", Number(e.target.value))} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>×</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex justify-between">
            <Button type="button" variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" /> Add Line</Button>
            <div className="flex gap-2">
              <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating..." : "Create Budget"}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Budget Detail Dialog ──────────────────────────────────────────────────────

function BudgetDetailDialog({ budgetId, open, onOpenChange }: { budgetId: string | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: budget, isLoading } = useBudgetById(budgetId || undefined);
  const { data: variance } = useBudgetVarianceReport(budgetId || undefined);
  const approveMutation = useApproveBudget();
  const notify = useNotification();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{budget?.budgetName || "Budget"}</DialogTitle></DialogHeader>
        {isLoading || !budget ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-muted-foreground">Fiscal Year</span><div className="font-semibold">{budget.fiscalYear}</div></div>
              <div><span className="text-muted-foreground">Frequency</span><div>{budget.frequencyName}</div></div>
              <div><span className="text-muted-foreground">Total Budgeted</span><div className="font-semibold">{fmt(budget.totalBudgeted)}</div></div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <div>{budget.isApproved ? <Badge className="bg-green-100 text-green-800">Approved</Badge> : <Badge variant="outline">Draft</Badge>}</div>
              </div>
            </div>

            {!budget.isApproved && (
              <PermissionGate permissions={["budgets.approve"]}>
                <Button size="sm" onClick={() => approveMutation.mutate(budget.id, {
                  onSuccess: () => notify.success("Budget approved"),
                  onError: (err) => notify.error(getErrorMessage(err)),
                })} disabled={approveMutation.isPending}>
                  <CheckCircle className="h-4 w-4 mr-1" /> Approve Budget
                </Button>
              </PermissionGate>
            )}

            {variance && (
              <>
                <h3 className="font-semibold mt-4">Variance Report</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card><CardContent className="pt-3 pb-2"><p className="text-xs text-muted-foreground">Budgeted</p><p className="font-bold">{fmt(variance.totalBudgeted)}</p></CardContent></Card>
                  <Card><CardContent className="pt-3 pb-2"><p className="text-xs text-muted-foreground">Actual</p><p className="font-bold">{fmt(variance.totalActual)}</p></CardContent></Card>
                  <Card><CardContent className="pt-3 pb-2"><p className="text-xs text-muted-foreground">Variance</p><p className="font-bold">{fmt(variance.totalVariance)}</p></CardContent></Card>
                  <Card><CardContent className="pt-3 pb-2"><p className="text-xs text-muted-foreground">Variance %</p><p className="font-bold">{variance.overallVariancePercent.toFixed(1)}%</p></CardContent></Card>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead><TableHead className="text-right">Budgeted</TableHead>
                      <TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variance.accountVariances.map((a) => (
                      <TableRow key={a.accountId}>
                        <TableCell className="text-sm">{a.accountCode} - {a.accountName}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmt(a.budgetedAmount)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmt(a.actualAmount)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmt(a.variance)}</TableCell>
                        <TableCell className="text-right text-sm">
                          <span className={a.variancePercent >= 0 ? "text-green-600" : "text-red-600"}>{a.variancePercent.toFixed(1)}%</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function BudgetsPage() {
  const { data: budgets, isLoading } = useBudgets();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Budgets</h1>
          <p className="text-muted-foreground">Create budgets and track variance against actuals</p>
        </div>
        <PermissionGate permissions={["budgets.create"]}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New Budget
          </Button>
        </PermissionGate>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : !budgets?.length ? (
            <div className="text-center py-8 text-muted-foreground">No budgets created yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Budget Name</TableHead><TableHead>Year</TableHead><TableHead>Frequency</TableHead>
                  <TableHead className="text-right">Total Budgeted</TableHead><TableHead>Status</TableHead>
                  <TableHead>Created</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {budgets.map((b) => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailId(b.id)}>
                    <TableCell className="font-medium">{b.budgetName}</TableCell>
                    <TableCell>{b.fiscalYear}</TableCell>
                    <TableCell>{b.frequencyName}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(b.totalBudgeted)}</TableCell>
                    <TableCell>{b.isApproved ? <Badge className="bg-green-100 text-green-800">Approved</Badge> : <Badge variant="outline">Draft</Badge>}</TableCell>
                    <TableCell className="text-sm">{fmtDate(b.createdAt)}</TableCell>
                    <TableCell><Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateBudgetDialog open={createOpen} onOpenChange={setCreateOpen} />
      <BudgetDetailDialog budgetId={detailId} open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} />
    </div>
  );
}
